import React, { useState, useMemo } from 'react';
import GameHistoryCard from './GameHistoryCard';
import EmptyState from '../../design-system/EmptyState';
import { QUEUE_FILTERS, getQueueType } from '../../constants';

export default function GameHistorySidebar({
  games = [],
  version,
  lpDeltasByGame = {},
  onImportClick,
  onAnalyzeGame,
  defaultLimit = 20,
  miniBlocks = [],
  userStats = null,
  userSettings = null
}) {
  const [queueFilter, setQueueFilter] = useState('all');
  const [limit, setLimit] = useState(defaultLimit);

  const filtered = useMemo(() => {
    const safeGames = Array.isArray(games) ? games : [];
    // v8.14 : games normalisées → `timestamp` est toujours défini (startTime sinon created_at)
    const gameDate = (g) => {
      const t = g.timestamp;
      const d = t ? new Date(t).getTime() : 0;
      return isNaN(d) ? 0 : d;
    };
    const sorted = [...safeGames].sort((a, b) => gameDate(b) - gameDate(a));
    if (queueFilter === 'all') return sorted;
    return sorted.filter(g => getQueueType(g.queueId) === queueFilter);
  }, [games, queueFilter]);

  const visible = filtered.slice(0, limit);
  const remaining = Math.max(0, filtered.length - limit);

  return (
    <div className="space-y-3">
      {/* Header sidebar : titre + filtres alignés horizontalement (sidebar large) */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[11px] tracking-[0.3em] uppercase text-zinc-400 font-bold">
          Historique · {filtered.length} game{filtered.length > 1 ? 's' : ''}
        </div>
        {/* Queue filters */}
        <div className="flex gap-1">
          {QUEUE_FILTERS.map(f => {
            const active = queueFilter === f.value;
            return (
              <button
                key={f.value}
                onClick={() => { setQueueFilter(f.value); setLimit(defaultLimit); }}
                className={`
                  px-2.5 py-1.5 text-[10px] font-bold tracking-wider transition
                  ${active
                    ? 'border border-cyan-400 text-cyan-400 bg-cyan-400/5'
                    : 'border border-zinc-800 text-zinc-500 hover:border-zinc-700 hover:text-zinc-400'
                  }
                `}
              >
                {f.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Liste */}
      {filtered.length === 0 ? (
        <EmptyState
          title="🎮 Pas encore de games"
          body={
            queueFilter === 'all'
              ? "Importe tes games pour qu'on commence à analyser."
              : `Aucune game en ${QUEUE_FILTERS.find(f => f.value === queueFilter)?.label}. Essaie un autre filtre.`
          }
          ctaLabel={queueFilter === 'all' ? "Importer" : undefined}
          onCtaClick={onImportClick}
          size="sm"
        />
      ) : (
        <div className="space-y-2">
          {visible.map((game) => {
            // V7.2.5 : lpDeltasByGame peut être un Map ou un objet, valeurs nombres OU {delta:...}
            let lpDelta = null;
            const raw = lpDeltasByGame instanceof Map
              ? lpDeltasByGame.get(game.id)
              : lpDeltasByGame?.[game.id];
            if (typeof raw === 'number') lpDelta = raw;
            else if (raw && typeof raw === 'object' && typeof raw.delta === 'number') lpDelta = raw.delta;

            return (
              <GameHistoryCard
                key={game.id || `${game.riot_match_id}-${game.timestamp}`}
                game={game}
                version={version}
                lpDelta={lpDelta}
                onAnalyze={onAnalyzeGame}
                miniBlocks={miniBlocks}
                userStats={userStats}
                userSettings={userSettings}
              />
            );
          })}

          {/* Load more */}
          {remaining > 0 && (
            <button
              onClick={() => setLimit(prev => prev + 20)}
              className="w-full border border-zinc-800 text-zinc-500 hover:border-zinc-700 hover:text-zinc-300 transition py-2.5 text-[10px] font-bold tracking-wider"
            >
              + {remaining} GAME{remaining > 1 ? 'S' : ''}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
